import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companyService, Company, UpdateCompanyProfileData } from '../services/companyService';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../stores/authStore';

const inputStyle: React.CSSProperties = {
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 400,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  display: 'block',
  marginBottom: 6,
};

const cardStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid rgba(92,78,51,0.15)',
  borderRadius: 6,
  padding: 24,
};

const primaryButtonStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const ghostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#5b5a52',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 400,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

export const CompanySettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { updateCompany } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Fetch company profile
  const { data: company, isLoading } = useQuery<Company>({
    queryKey: ['company', 'profile'],
    queryFn: companyService.getProfile,
  });

  const [formData, setFormData] = useState<UpdateCompanyProfileData>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    taxId: '',
    currency: 'USD',
  });

  // Update form data when company data is loaded
  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name,
        email: company.email,
        phone: company.phone || '',
        address: company.address || '',
        city: company.city || '',
        state: company.state || '',
        postalCode: company.postalCode || '',
        country: company.country || '',
        taxId: company.taxId || '',
        currency: company.currency,
      });
    }
  }, [company]);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateCompanyProfileData) => companyService.updateProfile(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['company', 'profile'] });
      updateCompany(variables);
      toast.success('Company profile updated successfully');
      setIsEditing(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update company profile');
    },
  });

  // Upload logo mutation
  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => companyService.uploadLogo(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'profile'] });
      toast.success('Logo uploaded successfully');
      setSelectedFile(null);
      setLogoPreview(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to upload logo');
    },
  });

  // Delete logo mutation
  const deleteLogoMutation = useMutation({
    mutationFn: () => companyService.deleteLogo(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'profile'] });
      toast.success('Logo deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete logo');
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate(formData);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = () => {
    if (selectedFile) {
      uploadLogoMutation.mutate(selectedFile);
    }
  };

  const handleDeleteLogo = () => {
    if (window.confirm('Are you sure you want to delete the company logo?')) {
      deleteLogoMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 256, background: '#f4efe2' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c' }}>Loading...</div>
      </div>
    );
  }

  const currentLogoUrl = company?.logoBase64 || (company?.logo ? companyService.getLogoUrl(company.logo) : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Page Header */}
      <div style={{ paddingBottom: 20, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 8 }}>
          Administration
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300, color: '#1c2033', lineHeight: 1 }}>
          Company Settings
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 300, color: '#8a8778', marginTop: 8 }}>
          Manage your company profile and branding
        </div>
      </div>

      {/* Logo Section */}
      <div style={cardStyle}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 400, color: '#1c2033', marginBottom: 20, paddingBottom: 12, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
          Company Logo
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 32 }}>
          <div style={{ flexShrink: 0 }}>
            {logoPreview || currentLogoUrl ? (
              <img
                src={logoPreview || currentLogoUrl || ''}
                alt="Company Logo"
                style={{ height: 128, width: 128, objectFit: 'contain', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 6, background: '#faf7ee' }}
                crossOrigin="anonymous"
              />
            ) : (
              <div style={{ height: 128, width: 128, border: '1px dashed rgba(92,78,51,0.25)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#faf7ee' }}>
                <svg style={{ height: 40, width: 40, color: '#8a8778' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>Upload Logo</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52', width: '100%' }}
              />
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#8a8778', marginTop: 6 }}>
                PNG, JPG, GIF up to 5MB
              </p>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {selectedFile && (
                <button
                  onClick={handleUploadLogo}
                  disabled={uploadLogoMutation.isPending}
                  style={{ ...primaryButtonStyle, opacity: uploadLogoMutation.isPending ? 0.5 : 1 }}
                >
                  {uploadLogoMutation.isPending ? 'Uploading...' : 'Upload'}
                </button>
              )}
              {currentLogoUrl && !selectedFile && (
                <button
                  onClick={handleDeleteLogo}
                  disabled={deleteLogoMutation.isPending}
                  style={{ ...ghostButtonStyle, opacity: deleteLogoMutation.isPending ? 0.5 : 1 }}
                >
                  {deleteLogoMutation.isPending ? 'Deleting...' : 'Delete Logo'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Company Profile Section */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingBottom: 12, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 400, color: '#1c2033' }}>
            Company Profile
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              style={ghostButtonStyle}
            >
              <svg style={{ display: 'inline', height: 12, width: 12, marginRight: 6, verticalAlign: 'middle' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
          )}
        </div>

        {isEditing ? (
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
              <div>
                <label style={labelStyle}>Company Name <span style={{ color: '#d97706' }}>*</span></label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Email <span style={{ color: '#d97706' }}>*</span></label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Tax ID</label>
                <input type="text" name="taxId" value={formData.taxId} onChange={handleInputChange} style={inputStyle} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Address</label>
                <input type="text" name="address" value={formData.address} onChange={handleInputChange} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>City</label>
                <input type="text" name="city" value={formData.city} onChange={handleInputChange} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>State</label>
                <input type="text" name="state" value={formData.state} onChange={handleInputChange} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Postal Code</label>
                <input type="text" name="postalCode" value={formData.postalCode} onChange={handleInputChange} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Country</label>
                <input type="text" name="country" value={formData.country} onChange={handleInputChange} style={inputStyle} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 16, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  if (company) {
                    setFormData({
                      name: company.name,
                      email: company.email,
                      phone: company.phone || '',
                      address: company.address || '',
                      city: company.city || '',
                      state: company.state || '',
                      postalCode: company.postalCode || '',
                      country: company.country || '',
                      taxId: company.taxId || '',
                      currency: company.currency,
                    });
                  }
                }}
                style={ghostButtonStyle}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateProfileMutation.isPending}
                style={{ ...primaryButtonStyle, opacity: updateProfileMutation.isPending ? 0.5 : 1 }}
              >
                {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <label style={labelStyle}>Company Name</label>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>{company?.name || '-'}</p>
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>{company?.email || '-'}</p>
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>{company?.phone || '-'}</p>
            </div>
            <div>
              <label style={labelStyle}>Tax ID</label>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>{company?.taxId || '-'}</p>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Address</label>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>{company?.address || '-'}</p>
            </div>
            <div>
              <label style={labelStyle}>City</label>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>{company?.city || '-'}</p>
            </div>
            <div>
              <label style={labelStyle}>State</label>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>{company?.state || '-'}</p>
            </div>
            <div>
              <label style={labelStyle}>Postal Code</label>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>{company?.postalCode || '-'}</p>
            </div>
            <div>
              <label style={labelStyle}>Country</label>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#1c2033', margin: 0 }}>{company?.country || '-'}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
